import { ROUTES } from 'constants/routes';
import { useAuthenticatedAxios } from 'hooks/useAuthenticatedAxios';
import { useMswManager } from 'lib/util/useMswManager';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, UseQueryResult } from 'react-query';
import { ErrorResponse } from 'react-router';
import { HrznGatewayMswBuilder } from 'stories/HrznGatewayMswBuilder';

export interface PartnerGetMyTransactionSummaryPatientStatementPayload {
  account: number;
  srcSystemKey: number;
}

interface ServiceDetail {
  invoiceNo: number;
  physician: string;
  facility: string;
  facilityAccountNo: string;
  patient: string;
  dos: string;
  serviceDescription: string;
  cpt: string;
  charge: number;
  payment: number;
  adjustment: number;
  patientResponsibility: number;
}

export interface PatientStatementResponse {
  billingProvider: string;
  billingAddress1: string;
  billingAddress2: string | null;
  billingCity: string;
  billingState: string;
  billingZipCode: string;
  taxID: string;
  accountNumber: string;
  statementDate: string;
  guarantorName: string;
  guarantorAddress1: string;
  guarantorAddress2: string | null;
  guarantorCity: string;
  guarantorState: string;
  guarantorZipCode: string;
  paymentUri: string;
  paymentPhone: string;
  amountDue: number;
  serviceDetails: ServiceDetail[];
}

export function usePartnerGetMyTransactionSummaryPatientStatement(
  accountNumber: PartnerGetMyTransactionSummaryPatientStatementPayload | null
): UseQueryResult<PatientStatementResponse[], ErrorResponse> {
  const Axios = useAuthenticatedAxios();
  const snackbar = useSnackbar();

  useMswManager(
    HrznGatewayMswBuilder.create().post(
      ROUTES.PartnerGetMyTransactionSummaryPatientStatement,
      {
        data: [
          {
            billingProvider: 'Sample Medical Group LLP',
            billingAddress1: 'PO BOX 123456',
            billingAddress2: null,
            billingCity: 'Sample City',
            billingState: 'ST',
            billingZipCode: '12345-6789',
            taxID: '123456789',
            accountNumber: 'A1234567',
            statementDate: '2023-12-31T00:00:00',
            guarantorName: 'DOE, JOHN',
            guarantorAddress1: '123 SAMPLE STREET',
            guarantorAddress2: null,
            guarantorCity: 'SAMPLETOWN',
            guarantorState: 'ST',
            guarantorZipCode: '12345',
            paymentUri: 'www.SamplePaymentPortal.com',
            paymentPhone: '(123) 456-7890',
            amountDue: 0.0,
            serviceDetails: [
              {
                invoiceNo: 10000001,
                physician: 'SMITH MD, J',
                facility: 'Sample General Hospital',
                facilityAccountNo: '100000000001',
                patient: 'DOE, JOHN',
                dos: '2023-12-31T00:00:00',
                serviceDescription: 'ELECTROCARDIOGRAM REPORT',
                cpt: '93010',
                charge: 144.0,
                payment: 31.74,
                adjustment: 112.26,
                patientResponsibility: 0.0
              },
              {
                invoiceNo: 10000001,
                physician: 'SMITH MD, J',
                facility: 'Sample General Hospital',
                facilityAccountNo: '100000000001',
                patient: 'DOE, JOHN',
                dos: '2023-12-31T00:00:00',
                serviceDescription: 'EMERGENCY DEPT VISIT',
                cpt: '99285',
                charge: 1608.0,
                payment: 553.37,
                adjustment: 1054.63,
                patientResponsibility: 0.0
              }
            ]
          }
        ]
      }
    )
  );

  return useQuery<PatientStatementResponse[], ErrorResponse>(
    [
      ROUTES.PartnerGetMyTransactionSummaryPatientStatement,
      accountNumber?.account ?? -1,
      accountNumber?.srcSystemKey ?? -1
    ],
    async () => {
      const { data } = await Axios({
        method: 'post',
        data: accountNumber,
        params: {
          route: ROUTES.PartnerGetMyTransactionSummaryPatientStatement
        }
      });
      return data;
    },
    {
      enabled: !!Object.keys(accountNumber ?? {}).length,
      onError: (err: ErrorResponse) => {
        snackbar.enqueueSnackbar(
          err.data ?? 'Failed to get patient statement',
          { variant: 'error' }
        );
      }
    }
  );
}
